"use client";

import { Calendar, Clock, X, Phone, User } from "lucide-react";
import { Card } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import type { AppointmentReminder } from "@/app/_module/contexts/AppointmentReminderContext";
import { formatTimeWithTimezone } from "@/app/(authenticated)/appointments/_modules/util/timezone";

interface AppointmentReminderWidgetProps {
  reminders: AppointmentReminder[];
  onStart: (appointmentId: string) => void;
  onSnooze: (appointmentId: string) => void;
  onDismiss: (appointmentId: string) => void;
}

function formatPhoneNumber(phone: string) {
  const cleaned = phone.replace(/\D/g, "");
  const digits = cleaned.length === 11 && cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export const AppointmentReminderWidget = ({
  reminders,
  onStart,
  onSnooze,
  onDismiss,
}: AppointmentReminderWidgetProps) => {
  if (reminders.length === 0) return null;

  return (
    <div className="appt-reminder-stack fixed top-20 right-[calc(1rem+env(safe-area-inset-right,0px))] z-[49] flex flex-col gap-3 w-80">
      {reminders.map((reminder) => {
        const isCritical = reminder.urgency === "critical";
        const tzInfo = formatTimeWithTimezone(reminder.startTime, reminder.leadState);

        return (
          <Card
            key={`${reminder.appointmentId}-${reminder.urgency}`}
            className={`shadow-lg border-2 animate-in slide-in-from-top-4 overflow-hidden ${
              isCritical ? "border-red-400" : "border-orange-400"
            }`}
          >
            <div
              className={`p-3 text-white ${
                isCritical
                  ? "bg-gradient-to-r from-red-500 to-red-600"
                  : "bg-gradient-to-r from-orange-500 to-orange-600"
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Calendar className={`h-5 w-5 shrink-0 ${isCritical ? "animate-pulse" : ""}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {reminder.minutesUntil <= 1
                        ? "Appointment starting now!"
                        : `Appointment in ${reminder.minutesUntil} min`}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-white/80 hover:text-white hover:bg-white/20 shrink-0"
                  onClick={() => onDismiss(reminder.appointmentId)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Client Info */}
              <div className="space-y-1 mb-3">
                <p className="text-xs opacity-90 truncate font-medium">
                  {reminder.title}
                </p>
                {reminder.leadName && (
                  <div className="flex items-center gap-1.5">
                    <User className="h-3 w-3 opacity-75 shrink-0" />
                    <p className="text-xs opacity-90 truncate">{reminder.leadName}</p>
                  </div>
                )}
                {reminder.leadPhone && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="h-3 w-3 opacity-75 shrink-0" />
                    <p className="text-xs opacity-75 font-mono">{formatPhoneNumber(reminder.leadPhone)}</p>
                  </div>
                )}
                {reminder.appointmentType && (
                  <p className="text-xs opacity-75 capitalize">
                    {reminder.appointmentType.replace(/-/g, " ")}
                  </p>
                )}
                {/* Timezone display */}
                {tzInfo && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3 opacity-75 shrink-0" />
                    {tzInfo.sameTimezone || !tzInfo.clientTime ? (
                      <p className="text-xs opacity-75">{tzInfo.agentTime}</p>
                    ) : (
                      <p className="text-xs opacity-75">
                        You: {tzInfo.agentTime} ({tzInfo.agentTz}) | Client: {tzInfo.clientTime} ({tzInfo.clientTz})
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0 text-xs h-8"
                  onClick={() => onSnooze(reminder.appointmentId)}
                >
                  Snooze 5m
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className={`flex-1 font-semibold text-xs h-8 ${
                    isCritical
                      ? "bg-white text-red-600 hover:bg-white/90"
                      : "bg-white text-orange-600 hover:bg-white/90"
                  }`}
                  onClick={() => onStart(reminder.appointmentId)}
                >
                  Start
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
